import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { profileService } from '../services/profileService';
import { Button, Card } from '../styles/GlobalStyles';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color);
  padding: var(--spacing-lg);
`;

const VerificationCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  text-align: center;
`;

const Title = styled.h1`
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const Message = styled.p`
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
  line-height: 1.6;
`;

const LoadingSpinner = styled.div`
  border: 4px solid var(--border-color);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-lg);

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  color: var(--success-color);
  margin-bottom: var(--spacing-lg);
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  color: var(--error-color);
  margin-bottom: var(--spacing-lg);
`;

export const VerifyEmailChangePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        await profileService.verifyEmailChange(token);
        setStatus('success');
        setMessage('Your email address has been successfully updated! You can now log in with your new email address.');
      } catch (error: any) {
        setStatus('error');
        const errorMessage = error.response?.data?.error?.message || 'Failed to verify email change. The link may have expired or already been used.';
        setMessage(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <Container>
      <VerificationCard>
        {status === 'loading' && (
          <>
            <LoadingSpinner />
            <Title>Verifying Email Change</Title>
            <Message>Please wait while we verify your email change...</Message>
          </>
        )}

        {status === 'success' && (
          <>
            <SuccessIcon>✓</SuccessIcon>
            <Title>Email Updated!</Title>
            <Message>{message}</Message>
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon>✕</ErrorIcon>
            <Title>Verification Failed</Title>
            <Message>{message}</Message>
            <Button onClick={() => navigate('/profile')}>
              Back to Profile
            </Button>
          </>
        )}
      </VerificationCard>
    </Container>
  );
};
